import HandymanOutlinedIcon from '@mui/icons-material/HandymanOutlined';
import { BaseWidget } from "./BaseWidget";
import ModulePlaceholder from "./_shared/ModulePlaceholder";
import templatePersonaNotice from "./_shared/templatePersonaNotice";
import NO_PERSONA_TEXT from "./_shared/noPersonaText";
import usePersonaWorkSkills from "./_shared/usePersonaWorkSkills";
import usePublicPersona from "./_shared/usePublicPersona";
import { publicWorkSkillsState } from "../../../../helpers/liveReportHelpers/publicPersonaState";
import { groeiRijenVoorWeergave, groeiToelichting, datumUI } from "../../../../helpers/arbeidsvaardighedenHelpers/groeiWeergave";
import GroeiPerThema from "../../../personas/arbeidsvaardigheden/GroeiPerThema";

// AI-CONTEXT: See docs/features/livereport-per-deelnemer.md
// AI-CONTEXT: See docs/features/arbeidsvaardigheden.md
// Arbeidsvaardigheden van de deelnemer van het rapport: per thema de groei van
// de eerste tot de laatste beoordeling, met dezelfde lijst als het tabblad
// (GroeiPerThema + groeiWeergave). Geen eigen groeiformule: groeiPerEffect.
//
// Lezen en tonen zijn gescheiden: PersonaWorkSkillsView krijgt `status` +
// `assessments`, uit usePersonaWorkSkills in de app of uit
// `personaData.workSkills` op de link (alleen datum en scores).

/**
 * @extends {BaseWidget}
 */
export class PersonaWorkSkillsWidget extends BaseWidget {

    getName() { return 'Arbeidsvaardigheden'; }
    getCategory() { return 'medewerkers'; }
    getRequiredModule() { return 'personas'; }
    getIcon() { return <HandymanOutlinedIcon />; }

    getControls() {
        return {};
    }

    render(props) {
        return <Renderer {...props} />;
    }
}

/**
 * De groei per thema zonder ophalen.
 *
 * @param {object} props
 * @param {import('./_shared/usePersonaWorkSkills').PersonaWorkSkillsStatus} props.status - Toestand van het ophalen
 * @param {Array<object>} props.assessments - WorkSkillsAssessments van de deelnemer, in elke volgorde
 * @returns {JSX.Element}
 */
export const PersonaWorkSkillsView = ({ status, assessments }) => {
    if (status === 'idle') {
        return <p className="persona-workskills-message">{NO_PERSONA_TEXT}</p>;
    }
    if (status === 'error') {
        return <p role="alert" className="persona-workskills-message">De beoordelingen arbeidsvaardigheden konden niet worden geladen.</p>;
    }
    if (status === 'missing') {
        return <p className="persona-workskills-message">Deze deelnemer bestaat niet meer</p>;
    }
    // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
    if (status !== 'ready') {
        return <p className="persona-workskills-message">Beoordelingen laden…</p>;
    }
    if (assessments.length === 0) {
        return <p className="persona-workskills-message">Deze deelnemer is nog niet beoordeeld op arbeidsvaardigheden.</p>;
    }
    // Pas vanaf twee beoordelingen valt er iets over verloop te zeggen; negen
    // streepjes zouden lezen als "niets gemeten".
    if (assessments.length === 1) {
        return (
            <p className="persona-workskills-message">
                {`Eén beoordeling (${datumUI(assessments[0].Datum)}). Groei is te zien vanaf de tweede beoordeling.`}
            </p>
        );
    }

    return (
        <GroeiPerThema
            titel="Groei per thema"
            toelichting={groeiToelichting(assessments)}
            rijen={groeiRijenVoorWeergave(assessments)}
            eenheid="punt"
        />
    );
};

/**
 * @returns {JSX.Element}
 */
const Renderer = () => {
    const { isPublic, personaData, personasModuleOn, personaId, templateStatus } = usePublicPersona();
    // Op de link en in een template leest de hook niets (`null`); de beoordelingen komen van de server.
    const live = usePersonaWorkSkills(isPublic || !personasModuleOn ? null : personaId);
    const { status, assessments } = isPublic && personaId ? publicWorkSkillsState(personaData) : live;

    const notice = templatePersonaNotice(templateStatus);
    if (notice) return notice;
    if (!personasModuleOn) {
        return <ModulePlaceholder mode="disabled" moduleLabel="Deelnemers" />;
    }
    if (personaId === null) {
        return <PersonaWorkSkillsView status="idle" assessments={[]} />;
    }
    return <PersonaWorkSkillsView status={status} assessments={assessments} />;
};

export default PersonaWorkSkillsWidget;
